import { describeFailure } from '@ns/plugin-arm-proxy-client';
import type { ReadField } from '@ns/plugin-picker-shared';
import {
  describeSourceMismatch,
  makeReadField,
  missingOption,
  toSubmitValidator,
  wrongShape,
} from '@ns/plugin-picker-shared';

import {
  AZURE_RESOURCE_GROUP_PICKER,
  AZURE_RESOURCE_PICKER,
  AZURE_SUBSCRIPTION_PICKER,
} from './field-names';
import { isPickerValue, type KnownKeys } from './field-spec';
import { MatchAuthorError } from './match';
import { queryResourcesOptions } from './query-resources-options';
import { PERMANENT_REFUSAL_CODES } from './query-state';
import {
  buildResourceGroupQuery,
  RESOURCE_GROUP_FIELDS,
  type ResourceGroupPickerUiOptions,
  selectResourceGroups,
} from './resource-group-query';
import {
  buildResourceQuery,
  describeMalformedResourceType,
  isResourceTypeShaped,
  RESOURCE_FIELDS,
  type ResourcePickerUiOptions,
  selectResources,
} from './resource-query';
import {
  AZURE_PICKER_API_VERSION,
  buildSubscriptionQuery,
  selectSubscriptions,
  SUBSCRIPTION_FIELDS,
  type SubscriptionPickerUiOptions,
} from './subscription-query';
import { readArmTransport } from './transport';
import type {
  AzureResource,
  AzureResourceGroup,
  AzureSubscription,
} from './types';
import { armQueryClient } from './use-query-resources';

/**
 * The reader this context can offer, in the MILD stance.
 *
 * @remarks
 * A different channel from the picker's: the validator gets `context.formData`
 * and has no `registry`, so it cannot tell a typo from a field the filler never
 * touched. Treating an unknown name as an author error would block a correct
 * template on submit, so here it reads as empty and the comparison drops.
 */
function readFieldOf(context: {
  formData: Record<string, unknown>;
}): ReadField {
  return makeReadField(
    context.formData,
    new Set(Object.keys(context.formData)),
    'empty',
  );
}

/*
 * The fields, besides `id`, by which the source names each picker value.
 *
 * A restored value whose row is on the offered page passes only when these
 * still match that row. A value whose row is absent from an incomplete page
 * passes without this comparison, because absence from part of the list proves
 * nothing. The resource group and the resource include `name`, because ARM
 * addresses them by name and the resource query is built from the group's
 * name. Display fields are left out.
 */
const SUBSCRIPTION_SOURCE_FIELDS = [
  'type',
  'subscriptionId',
] as const satisfies readonly KnownKeys<AzureSubscription>[];

const RESOURCE_GROUP_SOURCE_FIELDS = [
  'type',
  'name',
] as const satisfies readonly KnownKeys<AzureResourceGroup>[];

const RESOURCE_SOURCE_FIELDS = [
  'type',
  'subscriptionId',
  'resourceGroup',
  'name',
] as const satisfies readonly KnownKeys<AzureResource>[];

/** A value a validator reads before it queries: ready, still waiting on the filler, or blocked with a message. */
type Reading<T> =
  | { readonly kind: 'ready'; readonly value: T }
  | { readonly kind: 'waiting' }
  | { readonly kind: 'blocked'; readonly message: string };

/** Reads one non-empty text field of a referenced picker value. */
function readReferencedText(
  value: unknown,
  key: 'name' | 'subscriptionId',
  blockedMessage: string,
): Reading<string> {
  if (value === undefined || value === null) return { kind: 'waiting' };
  if (typeof value !== 'object' || Array.isArray(value)) {
    return { kind: 'blocked', message: blockedMessage };
  }
  const fields: Record<string, unknown> = { ...value };
  const text = fields[key];
  return typeof text === 'string' && text.length > 0
    ? { kind: 'ready', value: text }
    : { kind: 'blocked', message: blockedMessage };
}

/** Reads the resource-group picker's `subscriptionRef`. Never `waiting`. */
function readSubscriptionRef(subscriptionRef: unknown): Reading<string> {
  if (subscriptionRef === undefined) {
    return {
      kind: 'blocked',
      message: missingOption(
        'subscriptionRef',
        'it names the field holding the subscription',
      ),
    };
  }
  if (typeof subscriptionRef !== 'string') {
    return {
      kind: 'blocked',
      message: wrongShape(
        'subscriptionRef',
        'one field name, written as a plain word',
      ),
    };
  }
  return { kind: 'ready', value: subscriptionRef };
}

/** The resource picker's `ui:options`, checked and ready to query with. */
interface ResourcePickerSettings {
  readonly resourceGroupRef: string;
  readonly resourceType: string;
  readonly subscriptionRef: string;
}

/**
 * Reads the resource picker's `ui:options`. Never `waiting`.
 *
 * @remarks
 * Every missing option is reported before any option of the wrong shape, so
 * an author sees the errors in the same order as before.
 */
function readResourcePickerSettings(
  uiOptions: ResourcePickerUiOptions | undefined,
): Reading<ResourcePickerSettings> {
  const subscriptionRef = uiOptions?.subscriptionRef;
  const resourceGroupRef = uiOptions?.resourceGroupRef;
  const resourceType = uiOptions?.resourceType;
  if (subscriptionRef === undefined) {
    return {
      kind: 'blocked',
      message: missingOption(
        'subscriptionRef',
        'it names the field holding the subscription',
      ),
    };
  }
  if (resourceGroupRef === undefined) {
    return {
      kind: 'blocked',
      message: missingOption(
        'resourceGroupRef',
        'it names the field holding the resource group',
      ),
    };
  }
  if (resourceType === undefined) {
    return {
      kind: 'blocked',
      message: missingOption(
        'resourceType',
        'without a kind there is no sensible list, only everything at once',
      ),
    };
  }
  if (typeof subscriptionRef !== 'string') {
    return {
      kind: 'blocked',
      message: wrongShape(
        'subscriptionRef',
        'one field name, written as a plain word',
      ),
    };
  }
  if (typeof resourceGroupRef !== 'string') {
    return {
      kind: 'blocked',
      message: wrongShape(
        'resourceGroupRef',
        'one field name, written as a plain word',
      ),
    };
  }
  if (typeof resourceType !== 'string') {
    return {
      kind: 'blocked',
      message: wrongShape(
        'resourceType',
        'one piece of text, such as Microsoft.Network/routeTables',
      ),
    };
  }
  if (!isResourceTypeShaped(resourceType)) {
    return {
      kind: 'blocked',
      message: describeMalformedResourceType(resourceType),
    };
  }
  return {
    kind: 'ready',
    value: { resourceGroupRef, resourceType, subscriptionRef },
  };
}

/**
 * Builds a query, reading an author error as the message for the field.
 *
 * @remarks
 * Any other throw passes on, so the validator blocks it with the fixed message
 * and logs it, instead of showing its text.
 */
function readQuery(build: () => string): Reading<string> {
  try {
    return { kind: 'ready', value: build() };
  } catch (error) {
    if (error instanceof MatchAuthorError) {
      return { kind: 'blocked', message: error.message };
    }
    throw error;
  }
}

export const subscriptionValidator = toSubmitValidator<
  AzureSubscription,
  SubscriptionPickerUiOptions
>(
  async (submittedValue, context) => {
    if (
      !isPickerValue<AzureSubscription>(submittedValue, SUBSCRIPTION_FIELDS)
    ) {
      return 'The selected subscription is incomplete. Choose it again.';
    }

    const filter = context.uiSchema?.['ui:options']?.filter;
    const query = readQuery(() =>
      buildSubscriptionQuery(readFieldOf(context), filter),
    );
    if (query.kind === 'blocked') return query.message;
    if (query.kind !== 'ready') return;

    const answer = await armQueryClient.query(
      queryResourcesOptions(
        { apiVersion: AZURE_PICKER_API_VERSION, query: query.value },
        readArmTransport(context.apiHolder),
      ),
    );
    const selected = selectSubscriptions(answer);
    return describeSourceMismatch<AzureSubscription, AzureSubscription>({
      isCaseInsensitive: true,
      isComplete: !selected.page.hasMore,
      items: selected.items,
      sourceFields: SUBSCRIPTION_SOURCE_FIELDS,
      subject: 'subscription',
      submittedValue,
    });
  },
  {
    describeFailure,
    fieldName: AZURE_SUBSCRIPTION_PICKER,
    logPrefix: 'azure-pickers subscriptionValidator',
    permanentRefusalCodes: PERMANENT_REFUSAL_CODES,
    subject: 'subscription',
  },
);

export const resourceGroupValidator = toSubmitValidator<
  AzureResourceGroup,
  ResourceGroupPickerUiOptions
>(
  async (submittedValue, context) => {
    if (
      !isPickerValue<AzureResourceGroup>(submittedValue, RESOURCE_GROUP_FIELDS)
    ) {
      return 'The selected resource group is incomplete. Choose it again.';
    }

    const uiOptions = context.uiSchema?.['ui:options'];
    const reference = readSubscriptionRef(uiOptions?.subscriptionRef);
    if (reference.kind === 'blocked') return reference.message;
    if (reference.kind !== 'ready') return;
    const subscription = readReferencedText(
      context.formData[reference.value],
      'subscriptionId',
      'Select a valid subscription before selecting a resource group.',
    );
    if (subscription.kind === 'blocked') return subscription.message;
    if (subscription.kind !== 'ready') return;

    const query = readQuery(() =>
      buildResourceGroupQuery(readFieldOf(context), {
        exclude: uiOptions?.exclude,
        filter: uiOptions?.filter,
      }),
    );
    if (query.kind === 'blocked') return query.message;
    if (query.kind !== 'ready') return;

    const answer = await armQueryClient.query(
      queryResourcesOptions(
        {
          apiVersion: AZURE_PICKER_API_VERSION,
          query: query.value,
          subscriptions: [subscription.value],
        },
        readArmTransport(context.apiHolder),
      ),
    );
    const selected = selectResourceGroups(answer);
    return describeSourceMismatch<AzureResourceGroup, AzureResourceGroup>({
      isCaseInsensitive: true,
      isComplete: !selected.page.hasMore,
      items: selected.items,
      sourceFields: RESOURCE_GROUP_SOURCE_FIELDS,
      subject: 'resource group',
      submittedValue,
    });
  },
  {
    describeFailure,
    fieldName: AZURE_RESOURCE_GROUP_PICKER,
    logPrefix: 'azure-pickers resourceGroupValidator',
    permanentRefusalCodes: PERMANENT_REFUSAL_CODES,
    subject: 'resource group',
  },
);

export const resourceValidator = toSubmitValidator<
  AzureResource,
  ResourcePickerUiOptions
>(
  async (submittedValue, context) => {
    if (!isPickerValue<AzureResource>(submittedValue, RESOURCE_FIELDS)) {
      return 'The selected resource is incomplete. Choose it again.';
    }

    const settings = readResourcePickerSettings(
      context.uiSchema?.['ui:options'],
    );
    if (settings.kind === 'blocked') return settings.message;
    if (settings.kind !== 'ready') return;
    const parentMessage =
      'Select a valid subscription and resource group before selecting a resource.';
    const subscription = readReferencedText(
      context.formData[settings.value.subscriptionRef],
      'subscriptionId',
      parentMessage,
    );
    const resourceGroup = readReferencedText(
      context.formData[settings.value.resourceGroupRef],
      'name',
      parentMessage,
    );
    // Waiting outranks blocked: while either parent is still empty, the filler
    // has not finished, so a malformed sibling is no reason to block yet.
    if (subscription.kind === 'waiting' || resourceGroup.kind === 'waiting') {
      return;
    }
    if (subscription.kind === 'blocked') return subscription.message;
    if (resourceGroup.kind === 'blocked') return resourceGroup.message;

    const query = buildResourceQuery({
      resourceGroup: resourceGroup.value,
      resourceType: settings.value.resourceType,
    });
    const answer = await armQueryClient.query(
      queryResourcesOptions(
        {
          apiVersion: AZURE_PICKER_API_VERSION,
          query,
          subscriptions: [subscription.value],
        },
        readArmTransport(context.apiHolder),
      ),
    );
    const selected = selectResources(answer);
    return describeSourceMismatch<AzureResource, AzureResource>({
      isCaseInsensitive: true,
      isComplete: !selected.page.hasMore,
      items: selected.items,
      sourceFields: RESOURCE_SOURCE_FIELDS,
      subject: 'resource',
      submittedValue,
    });
  },
  {
    describeFailure,
    fieldName: AZURE_RESOURCE_PICKER,
    logPrefix: 'azure-pickers resourceValidator',
    permanentRefusalCodes: PERMANENT_REFUSAL_CODES,
    subject: 'resource',
  },
);
